import { TicketAttachment, TicketStatus } from "./types";

export const STATUS_OPTIONS: Array<{ value: TicketStatus; label: string }> = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In progress" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
];

export const RELATION_TYPE_OPTIONS = [
  { value: "related", label: "Related" },
  { value: "duplicate", label: "Duplicate" },
  { value: "blocks", label: "Blocks" },
  { value: "blocked_by", label: "Blocked by" },
] as const;

const PRIORITY_META: Record<number, { label: string; tone: "danger" | "warning" | "primary" | "secondary" }> = {
  1: { label: "Critical", tone: "danger" },
  2: { label: "High", tone: "warning" },
  3: { label: "Medium", tone: "primary" },
  4: { label: "Low", tone: "secondary" },
};

export const statusBadgeColor = (status: TicketStatus): string => {
  switch (status) {
    case "open":
      return "warning";
    case "in_progress":
      return "info";
    case "resolved":
      return "success";
    case "closed":
      return "secondary";
    default:
      return "light";
  }
};

export const getStatusLabel = (status: TicketStatus): string => {
  return STATUS_OPTIONS.find((option) => option.value === status)?.label || status;
};

export const getPriorityMeta = (priority: number) => {
  return PRIORITY_META[priority] || PRIORITY_META[3];
};

export const getUserInitials = (name?: string | null): string => {
  const parts = String(name || "System")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);

  return parts.map((part) => part[0]?.toUpperCase() || "").join("") || "SY";
};

export const getRatingLabel = (rating: number): string => {
  switch (rating) {
    case 1:
      return "Poor";
    case 2:
      return "Below expectations";
    case 3:
      return "Okay";
    case 4:
      return "Good";
    case 5:
      return "Excellent";
    default:
      return "Choose a rating";
  }
};

export const getConversationDateKey = (value?: string | null): string => {
  if (!value) return "unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "unknown";

  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
};

export const getConversationDateLabel = (value?: string | null): string => {
  if (!value) return "Unknown date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown date";

  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const isSameDay = (left: Date, right: Date) => (
    left.getFullYear() === right.getFullYear()
    && left.getMonth() === right.getMonth()
    && left.getDate() === right.getDate()
  );

  if (isSameDay(date, today)) {
    return "Today";
  }

  if (isSameDay(date, yesterday)) {
    return "Yesterday";
  }

  return date.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: date.getFullYear() === today.getFullYear() ? undefined : "numeric",
  });
};

export const formatFileSize = (sizeBytes?: number | null): string => {
  const size = Number(sizeBytes || 0);
  if (size <= 0) return "0 KB";
  if (size < 1024 * 1024) {
    return `${Math.max(1, Math.round(size / 1024))} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

export const getAttachmentIconClass = (attachment: TicketAttachment): string => {
  if (attachment.is_image) {
    return "bx bx-image-alt";
  }

  const mimeType = String(attachment.mime_type || "").toLowerCase();
  const fileName = String(attachment.original_name || "").toLowerCase();

  if (mimeType.includes("pdf") || fileName.endsWith(".pdf")) {
    return "bx bxs-file-pdf";
  }

  if (
    mimeType.includes("word")
    || fileName.endsWith(".doc")
    || fileName.endsWith(".docx")
    || fileName.endsWith(".txt")
  ) {
    return "bx bx-file";
  }

  if (
    mimeType.includes("sheet")
    || fileName.endsWith(".xls")
    || fileName.endsWith(".xlsx")
    || fileName.endsWith(".csv")
  ) {
    return "bx bx-spreadsheet";
  }

  if (
    mimeType.includes("zip")
    || mimeType.includes("compressed")
    || fileName.endsWith(".zip")
    || fileName.endsWith(".rar")
    || fileName.endsWith(".7z")
  ) {
    return "bx bx-archive";
  }

  return "bx bx-paperclip";
};

export const formatDateTime = (value?: string | null): string => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
};
